import StatisticsCard from '@app/src/pages/Dashboards/StatisticsCard'
import { BenchmarkGroupType } from '@app/src/types/benchmark'
import { InfoOutlined } from '@mui/icons-material'
import { Box, IconButton, Stack, Tooltip, Typography } from '@mui/material'
import React from 'react'
import { useIntl } from 'react-intl'
import type { KpiBadge, KpiBar, KpiCardState, KpiMetric } from '../benchmark/kpiCardLogic'
import { statusMessage } from '../benchmark/kpiCardLogic'
import { peerGroupOptionMessageId } from '../benchmark/peerGroups'
import { clampPercent } from '../profileLogic'
import KpiBadgeChip from './KpiBadgeChip'
import KpiComparisonText, { useBenchmarkMeanLine } from './KpiComparisonText'
import PeerMeanMarker from './PeerMeanMarker'
import { TONE_COLOR } from './profileColors'

interface BenchmarkKpiCardProps {
  metric: KpiMetric
  state: KpiCardState
  unit: string
  badge?: KpiBadge
  bar?: KpiBar
  peerGroup: BenchmarkGroupType
  isLoading: boolean
  onClick?: () => void
}

const BAR_HEIGHT = 8

const Bar: React.FC<{ bar: KpiBar; markerLabel?: string }> = ({ bar, markerLabel }) => (
  <Box position="relative" height={BAR_HEIGHT} borderRadius={999} bgcolor="grey.300">
    {bar.fill > 0 && (
      <Box
        position="absolute"
        top={0}
        bottom={0}
        left={0}
        width={`${clampPercent(bar.fill)}%`}
        minWidth={BAR_HEIGHT}
        borderRadius={999}
        bgcolor={TONE_COLOR[bar.tone]}
      />
    )}
    {bar.marker !== undefined && <PeerMeanMarker percent={bar.marker} label={markerLabel} />}
  </Box>
)

const BenchmarkKpiCard: React.FC<BenchmarkKpiCardProps> = ({
  metric,
  state,
  unit,
  badge,
  bar,
  peerGroup,
  isLoading,
  onClick,
}) => {
  const { formatMessage, formatNumber } = useIntl()
  const isNotChecked = state.kind === 'notChecked'
  const comparison = state.kind === 'noBenchmark' ? undefined : state.comparison
  const filterLabel = formatMessage({ id: peerGroupOptionMessageId(peerGroup) })
  const isAllCompanies = peerGroup === BenchmarkGroupType.Network

  const meanLine = useBenchmarkMeanLine(peerGroup, comparison)
  const status = statusMessage(metric, state, filterLabel)
  const info = comparison
    ? formatMessage(
        { id: isAllCompanies ? 'sustainabilityProfile.kpi.infoAllCompanies' : 'sustainabilityProfile.kpi.info' },
        { count: comparison.peerCount, filter: filterLabel },
      )
    : formatMessage(
        {
          id: isAllCompanies
            ? 'sustainabilityProfile.kpi.infoAllCompaniesUnavailable'
            : 'sustainabilityProfile.kpi.infoUnavailable',
        },
        { filter: filterLabel },
      )

  return (
    <StatisticsCard
      title={formatMessage({ id: `sustainabilityProfile.kpi.${metric}.title` })}
      action={
        <Tooltip title={info} arrow>
          <IconButton size="small">
            <InfoOutlined />
          </IconButton>
        </Tooltip>
      }
      justifyContent="left"
      withBorder={isNotChecked}
      loading={{ isLoading, skeleton: <Typography variant="kpi">–</Typography> }}
      onClick={onClick}
      sx={{
        pb: 3,
        flex: 1,
        minWidth: 0,
        borderRadius: 2,
        bgcolor: isNotChecked ? 'transparent' : 'background.paper',
        ...(onClick && { cursor: 'pointer', '&:hover': { bgcolor: 'grey.50' } }),
      }}
    >
      <Stack width="100%" spacing={2} mt={2}>
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={2}>
          <Typography variant="kpi" component="p" color={isNotChecked ? 'text.disabled' : 'text.primary'}>
            {state.kind === 'notChecked' ? (
              '–'
            ) : (
              <>
                {formatNumber(state.value, { maximumFractionDigits: 1 })}
                <Typography component="span" variant="inherit" color="text.disabled">
                  {unit}
                </Typography>
              </>
            )}
          </Typography>
          {badge && <KpiBadgeChip badge={badge} />}
        </Stack>

        <KpiComparisonText meanLine={meanLine} status={status} isMuted={isNotChecked} />

        {bar && <Bar bar={bar} markerLabel={meanLine} />}
      </Stack>
    </StatisticsCard>
  )
}

export default BenchmarkKpiCard
